import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';

// Step 6. What is offered immediately after this product is added. The shared
// default is right nearly always, so it is presented as the default.
const Upsell = ({ product, choices, commit }) => {
  const chosen = product.upsell_steps || [];
  const override = !!product.upsell_override;
  const labelFor = (key) => (choices.upsell_steps.find((x) => x.key === key) || {}).label || key;
  // Two kinds of set, one join table underneath: add-ons (a checkbox in the chain)
  // and personalised Printess items (a gallery). Empty means every set of that kind,
  // which is what the store does with no rows (#5423, #5328).
  const setKinds = [
    {
      key: 'addon_product_ids',
      title: 'Which add-ons are offered',
      sets: choices.addon_sets || [],
      chosen: product.addon_product_ids || [],
    },
    {
      key: 'printess_product_ids',
      title: 'Which personalised items are offered',
      sets: choices.printess_sets || [],
      chosen: product.printess_product_ids || [],
    },
  ];

  const save = (nextOverride, nextSteps) => commit({
    override: nextOverride ? '1' : '0',
    upsell_steps: nextSteps.map(String),
  });

  // Sent on its own so a change to the sets cannot be read as a change to the chain,
  // and vice versa -- the server only touches the links when the key is present.
  // BOTH kinds go every time: the server replaces the whole list, so a kind left out
  // of the request would have to be inferred rather than stated.
  const saveSets = (key, ids) => commit({
    override: override ? '1' : '0',
    upsell_steps: chosen.map(String),
    ...Object.fromEntries(setKinds.map((k) => [
      k.key, (k.key === key ? ids : k.chosen).map(String),
    ])),
  });

  const toggleSet = (kind, id) => saveSets(
    kind.key,
    kind.chosen.includes(id) ? kind.chosen.filter((x) => x !== id) : [...kind.chosen, id],
  );

  return (
    <Stack spacing={1}>
      <RadioGroup value={override ? 'own' : 'shared'}>
        <FormControlLabel
          value="shared"
          control={<Radio onChange={() => save(false, [])} />}
          sx={{ alignItems: 'flex-start', m: 0 }}
          label={(
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>Use the usual sequence</Typography>
              <Typography variant="caption" color="text.secondary">
                {choices.inherited_upsell?.length
                  ? `Currently: ${choices.inherited_upsell.map(labelFor).join(' → ')}. `
                  : 'Currently nothing is offered after adding to the cart. '}
                Managed for all products under Upsell flow.
              </Typography>
            </Box>
          )}
        />
        <FormControlLabel
          value="own"
          control={<Radio onChange={() => save(true, chosen)} />}
          sx={{ alignItems: 'flex-start', m: 0, mt: 1 }}
          label={(
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body2" fontWeight={600}>Something different for this product</Typography>
              <Typography variant="caption" color="text.secondary">
                Pick what to offer. Pick nothing to go straight to the cart — right for a one-off
                item like a graduation card.
              </Typography>
            </Box>
          )}
        />
      </RadioGroup>

      {/* display: flex, NOT block. FormControlLabel is inline-flex; forcing it to
          block stops the checkbox and the label being flex items, and the label is a
          Typography -- which renders a <p> -- so it dropped onto its own line under
          the box. flex keeps each row full width AND the pair side by side. */}
      <Box sx={{ pl: 4 }}>
        {choices.upsell_steps.map((step) => (
          <FormControlLabel
            key={step.key}
            sx={{ display: 'flex' }}
            control={(
              <Checkbox
                disabled={!override}
                checked={chosen.includes(step.key)}
                onChange={(e) => save(true, e.target.checked
                  ? [...chosen, step.key]
                  : chosen.filter((k) => k !== step.key))}
              />
            )}
            label={<Typography variant="body2">{step.label}</Typography>}
          />
        ))}
      </Box>

      {/* Which sets the steps above show. Separate from whether a step runs at all:
          the chain decides that, these decide what is in it. The legacy flow has had
          this per package for years (extra_assignments, 425 rows); the new flow
          offered every set to every product until #5423. */}
      {setKinds.filter((kind) => kind.sets.length).map((kind) => (
        <Box key={kind.key}>
          <Typography variant="subtitle2">{kind.title}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {kind.chosen.length
              ? 'Only these, in this order.'
              : 'Every set, in catalog order. Tick some to narrow it to those.'}
          </Typography>
          {kind.sets.map((set) => (
            <FormControlLabel
              key={set.id}
              sx={{ display: 'flex' }}
              control={(
                <Checkbox
                  checked={kind.chosen.includes(set.id)}
                  onChange={() => toggleSet(kind, set.id)}
                />
              )}
              label={(
                <Typography variant="body2">
                  {set.name}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {` · ${set.items} item${set.items === 1 ? '' : 's'}`}
                  </Typography>
                </Typography>
              )}
            />
          ))}
        </Box>
      ))}
    </Stack>
  );
};

Upsell.propTypes = stepPropTypes;
Upsell.defaultProps = stepDefaultProps;

export default Upsell;
